// /opt/isp_erp/frontend/src/components/ftth/ElementoPanel.jsx
import React, { useState, useEffect } from 'react';
import {
  Drawer, Box, Typography, TextField, Button, Stack, Divider,
  IconButton, Chip, Alert, FormControl, InputLabel, Select,
  MenuItem, CircularProgress, Grid, Avatar, Dialog, DialogTitle,
  DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { ftthGeoAPI } from '../../api/ftth_geo.api';
import axios from '../../api/axios';

const TIPOS = [
  'OLT', 'ODF',
  'SPLITTER_PRIMARIO', 'SPLITTER_SECUNDARIO', 'SPLITTER',
  'NAP', 'CTO', 'CAJA_EMPALME', 'CAJA_DISTRIBUCION',
  'POSTE', 'CAMARA', 'DUCTO', 'TORRE',
  'RESERVA', 'CLIENTE', 'OTRO',
];

const ESTADOS = ['PLANIFICADO', 'EN_CONSTRUCCION', 'ACTIVO', 'MANTENIMIENTO', 'FUERA_SERVICIO'];

const RATIOS = ['', '1:2', '1:4', '1:8', '1:16', '1:32', '1:64'];

export default function ElementoPanel({ elemento, open, onClose, onUpdated, onDeleted }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const fileInputRef = React.useRef();

  useEffect(() => {
    if (elemento) {
      setForm({ ...elemento });
    }
  }, [elemento]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleGuardar = async () => {
    if (!elemento) return;
    setSaving(true);
    setError('');
    try {
      const payload = { ...form };
      delete payload.id;
      delete payload.creado_en;
      delete payload.actualizado_en;
      delete payload.nap_id;
      // Limpiar tipos no numéricos
      ['latitud', 'longitud', 'altitud', 'altura_m'].forEach((k) => {
        if (payload[k] !== null && payload[k] !== undefined && payload[k] !== '') {
          payload[k] = parseFloat(payload[k]);
        }
      });
      ['capacidad', 'usados', 'puertos_entrada', 'puertos_salida',
       'total_puertos_pon', 'puertos_pon_usados'].forEach((k) => {
        if (payload[k] !== null && payload[k] !== undefined && payload[k] !== '') {
          payload[k] = parseInt(payload[k]);
        }
      });

      const res = await ftthGeoAPI.actualizarElemento(elemento.id, payload);
      setSuccess('Cambios guardados');
      if (onUpdated) onUpdated(res);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleEliminar = async () => {
    if (!elemento) return;
    setDeleting(true);
    setError('');
    try {
      await ftthGeoAPI.eliminarElemento(elemento.id);
      setConfirmDelete(false);
      if (onDeleted) onDeleted(elemento);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al eliminar');
    } finally {
      setDeleting(false);
    }
  };

  const handleSubirImagen = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !elemento) return;
    setUploading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('archivo', file);
      const res = await axios.post(
        `/ftth/geo/elementos/${elemento.id}/imagen`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setForm({ ...form, imagen_url: res.data.imagen_url });
      setSuccess('Imagen subida');
      if (onUpdated) onUpdated({ ...elemento, imagen_url: res.data.imagen_url });
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al subir imagen');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  if (!elemento) return null;

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: { xs: '100%', sm: 480 } } }}
      >
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {form.tipo} — {form.codigo}
          </Typography>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Box>
        <Divider />

        <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

          {/* Foto del elemento */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Avatar
              src={form.imagen_url ? `${form.imagen_url}` : undefined}
              sx={{ width: 64, height: 64, bgcolor: '#e3f2fd' }}
              variant="rounded"
            >
              {!form.imagen_url && '📷'}
            </Avatar>
            <Box>
              <input
                ref={fileInputRef}
                hidden
                type="file"
                accept="image/*"
                onChange={handleSubirImagen}
              />
              <Button
                size="small"
                variant="outlined"
                startIcon={<PhotoCameraIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? 'Subiendo...' : (form.imagen_url ? 'Cambiar foto' : 'Subir foto')}
              </Button>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo</InputLabel>
                <Select value={form.tipo || ''} name="tipo" label="Tipo" onChange={handleChange}>
                  {TIPOS.map((t) => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Código" name="codigo"
                value={form.codigo || ''} onChange={handleChange} />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Estado</InputLabel>
                <Select value={form.estado || 'PLANIFICADO'} name="estado" label="Estado" onChange={handleChange}>
                  {ESTADOS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Nombre" name="nombre"
                value={form.nombre || ''} onChange={handleChange} />
            </Grid>

            <Grid item xs={6}>
              <TextField fullWidth size="small" type="number" label="Latitud" name="latitud"
                value={form.latitud ?? ''} onChange={handleChange} inputProps={{ step: 0.000001 }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="number" label="Longitud" name="longitud"
                value={form.longitud ?? ''} onChange={handleChange} inputProps={{ step: 0.000001 }} />
            </Grid>

            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Dirección" name="direccion"
                value={form.direccion || ''} onChange={handleChange} />
            </Grid>

            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Zona" name="zona"
                value={form.zona || ''} onChange={handleChange} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Sector" name="sector"
                value={form.sector || ''} onChange={handleChange} />
            </Grid>

            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Ciudad" name="ciudad"
                value={form.ciudad || ''} onChange={handleChange} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Referencia" name="referencia"
                value={form.referencia || ''} onChange={handleChange} />
            </Grid>

            <Grid item xs={12}>
              <Divider><Typography variant="caption">Datos técnicos</Typography></Divider>
            </Grid>

            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Fabricante" name="fabricante"
                value={form.fabricante || ''} onChange={handleChange} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Modelo" name="modelo"
                value={form.modelo || ''} onChange={handleChange} />
            </Grid>

            <Grid item xs={12}>
              <TextField fullWidth size="small" label="N° Serie" name="numero_serie"
                value={form.numero_serie || ''} onChange={handleChange} />
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Ratio Splitter</InputLabel>
                <Select value={form.ratio_splitter || ''} name="ratio_splitter"
                  label="Ratio Splitter" onChange={handleChange}>
                  {RATIOS.map((r) => <MenuItem key={r} value={r}>{r || '—'}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={3}>
              <TextField fullWidth size="small" type="number" label="E" name="puertos_entrada"
                value={form.puertos_entrada ?? 0} onChange={handleChange} />
            </Grid>
            <Grid item xs={3}>
              <TextField fullWidth size="small" type="number" label="S" name="puertos_salida"
                value={form.puertos_salida ?? 0} onChange={handleChange} />
            </Grid>

            <Grid item xs={6}>
              <TextField fullWidth size="small" type="number" label="Capacidad" name="capacidad"
                value={form.capacidad ?? 0} onChange={handleChange} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="number" label="Usados" name="usados"
                value={form.usados ?? 0} onChange={handleChange} />
            </Grid>

            <Grid item xs={6}>
              <TextField fullWidth size="small" type="number" label="Altura (m)" name="altura_m"
                value={form.altura_m ?? ''} onChange={handleChange} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Material" name="material"
                value={form.material || ''} onChange={handleChange} />
            </Grid>

            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Propietario" name="propietario"
                value={form.propietario || ''} onChange={handleChange} />
            </Grid>

            <Grid item xs={12}>
              <Divider><Typography variant="caption">OLT (si aplica)</Typography></Divider>
            </Grid>

            <Grid item xs={6}>
              <TextField fullWidth size="small" label="IP Gestión" name="ip_gestion"
                value={form.ip_gestion || ''} onChange={handleChange} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="number" label="Puertos PON" name="total_puertos_pon"
                value={form.total_puertos_pon ?? 0} onChange={handleChange} />
            </Grid>

            <Grid item xs={12}>
              <Divider><Typography variant="caption">Notas</Typography></Divider>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth multiline minRows={3} size="small"
                label="Observaciones" name="observaciones"
                value={form.observaciones || ''} onChange={handleChange}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider />
        <Box sx={{ p: 2, display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setConfirmDelete(true)}
            disabled={deleting}
          >
            Eliminar
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={handleGuardar}
            disabled={saving}
          >
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </Box>
      </Drawer>

      {/* Confirmar eliminación */}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>¿Eliminar {elemento?.codigo}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Vas a eliminar <b>{elemento?.codigo}</b> ({elemento?.tipo}).
            <br />
            Los cables que lo tocan también se eliminarán.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)} disabled={deleting}>Cancelar</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleEliminar}
            disabled={deleting}
          >
            {deleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
